import React, { useEffect, useMemo } from 'react';
import { Form2 } from '../primitives/Forms2';
import { useContactsList } from './data/useContactsList';
import { useCRMContext } from './CRMContext';
import { TableForFields } from '../schemed';
import { Buttons, LoadingIndicator, MoreActionsMenuButton, OccupyFreeSpace, SearchField, useWindowHotkey } from '../primitives';
import { Checkbox, IconButton, Stack, Typography } from '@mui/material';
import { Add, Close, RefreshOutlined } from '@mui/icons-material';
import { ContactEditDrawer } from './ContactEditDrawer';
import { CRMMenu } from './Elements/CRMMenu';
import { useProcessQueryParam } from '../../hooks/useProcessQueryParam';
import { DescribedAddressLinks } from './Elements/DescribedAddressList';
import { ContactNameWithNotices } from './Elements/Primitives';
import { useEnsureTagsLoaded } from './CRMTagsContext';
import { Tags } from './Elements/CRMTags';
import { OrgLinkChip } from './Elements/OrgLinkChip';
import { ImportContactsDialog } from './Elements/ImportContactsDialog';
import { MergeContactsDialog } from './Elements/MergeContactsDialog';
import { ExportContactsDialog } from './Elements/ExportContactsDialog';
import { CRMGeoContextProvider } from './CRMGeoContext';
import { CreateContactDialog } from './Elements/CreateContactDialog';
import { ContactsFiltersPicker } from './Elements/Contacts/ContactsFiltersPicker';
import { UnifyGeoNamesDialog } from './Elements/Contacts/UnifyGeoNamesDialog';
import { ContactsBatchUpdateDialog } from './Elements/Contacts/ContactsBatchUpdateDialog';
import { CRMCategoryConfig } from './types';
import { useAutoSuggestionsStore } from '../primitives/AutoSuggests';
import { crmAttributeSuggestionsKey } from './Elements/Contacts/CustomAttributes/CustomAttributesEditors';

interface Props {}

export const ContactsPage = (props: Props) => {
  const ctx = useCRMContext();
  const data = useContactsList(ctx.apiPath);

  useWindowHotkey("alt+n", () => data.newRecord.startEditing());

  useProcessQueryParam("id",
    v => data.findContact(v).then(record => {
      data.editRecord.startEditing(record);
    }),
  );

  useEnsureTagsLoaded();

  const customAttributesSuggestions = ctx.settings.custom_attributes_suggestions;
  const autoSuggestionsStore = useAutoSuggestionsStore();
  useEffect(() => {
    if (customAttributesSuggestions) {
      const adjusted: Record<string, string[]> = {};
      Object.entries(customAttributesSuggestions).forEach(([k, v]) => {
        adjusted[crmAttributeSuggestionsKey(k)] = v;
      });
      autoSuggestionsStore.setOptionsBatch(adjusted);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customAttributesSuggestions]);

  const canExport = ctx.canExport;

  const categoriesMap = useMemo(() => {
    return (ctx.settings.contact_categories || []).reduce<Record<string, CRMCategoryConfig>>((r, v) => {
      r[v.code] = v;
      return r;
    }, {});
  }, [ctx.settings.contact_categories]);

  const hasSelection = data.selection.count > 0;

  return (<>
    <CRMGeoContextProvider>
    <Form2
      noTitle
      background="panel"
      fitFullHeight
      dimmed={data.editRecord.isEditing}
      formHeaderProps={{
        onClick: () => data.editRecord.cancel(),
      }}
      headerItems={<>
        <CRMMenu title="Контакты" current="contacts" />

        <IconButton size="small" color="primary" onClick={() => data.newRecord.startEditing()}>
          <Add />
        </IconButton>

        <OccupyFreeSpace />

        {data.isLoading && <LoadingIndicator sizeVariant="s" />}
        
        {hasSelection && (
          <Stack direction="row" alignItems="flex-end" spacing={0.5} alignSelf="flex-end" sx={{ mb: 0.5, mr: 1 }}>
            <Typography variant="caption" color="text.secondary">
              Выбрано: {data.selection.count}
            </Typography>
            <IconButton 
              size="small" 
              onClick={() => data.selection.clear()}
              sx={{ p: 0.25 }}
              title="Сбросить выбор"
            >
              <Close sx={{ fontSize: 16 }} />
            </IconButton>
          </Stack>
        )}

        <Typography variant="caption" color="textSecondary" alignSelf="flex-end" style={{ marginBottom: 4 }}>
          {data.count.data.total_records}
        </Typography>
        
        <IconButton size="small" color="primary" onClick={() => data.reload()}>
          <RefreshOutlined />
        </IconButton>

        <ContactsFiltersPicker
          filters={data.extraFilters}
          onUpdateFilters={c => data.setExtraFilters(x => ({ ...x, ...c }))}
          clearFilters={() => data.clearExtraFilters()}
          filteredRecordsCount={data.count.data.total_records}
          contactSchema={data.schema}
        />

        <SearchField
          filter={data.filter.filter}
          setFilter={data.filter.setFilter}
          noButton
          autoFocus
        />

        <MoreActionsMenuButton
          actions={[
            ["import", "импорт", () => data.importContacts.open()],
            canExport ?
            ["export", <Buttons>{data.exportContacts.start.isRunning && <LoadingIndicator sizeVariant="s" />} экспорт</Buttons>, () => data.exportContacts.start.run()]
            : null,
            ["unifygeo", "переименование географии", () => data.unifyGeo.start()],
            ["batchupdate", "изменить выбранные", () => data.batchUpdate.start.run()],
          ]}
        />
      </>}
    >
      <TableForFields
        data={data.data}
        fields={[
          ["actions", { label: " ", width: 50 }],
          ["name"],
          ["organization_name"],
          ["geo", { label: "География" }],
          Object.keys(categoriesMap).length
            ? ["categories"]
            : null,
          ["tags"],
          ["email"],
          // ["phone"],
        ]}
        fieldElement={f => {
          switch(f) {
            case "actions":
              if (data.merge.isMergeStarted) {
                return r => <>
                  {r._id !== data.merge.targetContact?._id &&
                   r._id !== data.merge.sourceContact?._id &&
                    <IconButton size="small" onClick={e => { e.stopPropagation(); data.merge.addSource(r); }}><Add /></IconButton>}
                </>;
              } else if (hasSelection) {
                return r => (
                  <Checkbox
                    size="small"
                    checked={data.selection.isSelected(r._id)}
                    onClick={e => e.stopPropagation()}
                    onChange={() => data.selection.toggle(r)}
                    sx={{ p: 0.5 }}
                  />
                );
              } else {
                return r => (
                  <MoreActionsMenuButton
                    size="small"
                    stopPropagation
                    actions={[
                      ["select", "Выбрать", () => data.selection.select(r)],
                      ["merge", "Слияние", () => data.merge.startMerge(r)]
                    ]}
                  />
                );
              }
            case "name":
              return r => <ContactNameWithNotices contact={r} />;
            case "email":
              return r => <span>
                <DescribedAddressLinks items={r.emails} showMainOnly />
              </span>;
            case "phone":
              return r => <span>
                <DescribedAddressLinks items={r.phones} showMainOnly />
              </span>;
            case "organization_name":
              return r => {
                const orgNames = r.organization_name?.split(";")?.map(s => s.trim()) || []; 
                return (
                  <Stack spacing={1} useFlexGap direction="row" flexWrap="wrap">
                    {orgNames.map(org => (
                      <OrgLinkChip
                        key={org}
                        organizationName={org}
                      />
                    ))}
                  </Stack>
                );
              };
            case "geo":
              return r => {
                const geos = [...(r.geo_country || []), ...(r.geo_city || [])];
                return (
                  <Stack spacing={1} useFlexGap direction="row" flexWrap="wrap">
                    {geos.map(geo => (
                      <OrgLinkChip
                        key={geo}
                        organizationName={geo}
                      />
                    ))}
                  </Stack>
                );
              };
            case "categories":
              return r => {
                const categories = (r.categories || []).map(c => categoriesMap[c]?.label || c);
                return (
                  <Stack spacing={1} useFlexGap direction="row" flexWrap="wrap">
                    {categories.map(category => (
                      <OrgLinkChip
                        key={category}
                        organizationName={category}
                      />
                    ))}
                  </Stack>
                );
              };
            case "tags":
              return r => (
                <Tags
                  tagIds={r.tags}
                  onTagClick={(e, t) => {
                    e.stopPropagation();
                    data.setExtraFilters(x => x.tags?.includes(t._id)
                      ? x
                      : ({ ...x, tags: [...(x.tags || []), t._id] }));
                  }}
                />
              );
          }
        }}
        onRowClick={r => data.editRecord.startEditing({ ...r })}
        schema={data.schema}
        tailAnchor={data.limitTrigger.pagingAnchor}
      />
    </Form2>

    <ContactEditDrawer 
      schema={data.schema} 
      editRecord={data.editRecord}
      removeRecord={data.remove.run}
    />

    <CreateContactDialog
      data={data.newRecord}
      schema={data.schema}
    />

    <ImportContactsDialog
      data={data.importContacts}
      onComplete={() => data.reload()}
      schema={data.schema}
    />

    <ExportContactsDialog
      data={data.exportContacts}
    />

    <MergeContactsDialog
      merge={data.merge}
      onComplete={() => data.reload()}
    />

    <UnifyGeoNamesDialog
      data={data.unifyGeo}
    />

    <ContactsBatchUpdateDialog
      data={data.batchUpdate}
      schema={data.schema}
    />
  </CRMGeoContextProvider>
  </>);
};